import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../../../../components/feature/Navbar';
import Footer from '../../../../components/feature/Footer';

const DefenderForEndpointPage = () => {
  const CodeBlock = ({ title, code }: { title: string; code: string }) => {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
      navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    return (
      <div className="bg-gray-900 rounded-xl p-6 overflow-x-auto">
        <div className="flex items-center justify-between mb-4">
          <span className="text-gray-400 text-sm">{title}</span>
          <button
            onClick={handleCopy}
            className="flex items-center space-x-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap
              bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={copied ? 'ri-check-line' : 'ri-file-copy-line'}></i>
            </div>
            <span>{copied ? 'Kopyalandı!' : 'Kopyala'}</span>
          </button>
        </div>
        <pre className="text-sm text-gray-100">{code}</pre>
      </div>
    );
  };

  const capabilities = [
    {
      icon: 'ri-shield-check-line',
      title: 'Tehdit Tespiti',
      description: 'Davranış analizi, makine öğrenimi ve tehdit istihbaratı ile gelişmiş tehditleri tespit edin',
      color: 'from-[#0078D4] to-[#005A9E]',
    },
    {
      icon: 'ri-search-eye-line',
      title: 'Tehdit Araştırma',
      description: 'Advanced Hunting ile KQL sorguları kullanarak derinlemesine tehdit analizi yapın',
      color: 'from-[#00BCF2] to-[#0078D4]',
    },
    {
      icon: 'ri-shield-flash-line',
      title: 'Otomatik Yanıt',
      description: 'Automated Investigation & Response (AIR) ile tehditlere otomatik müdahale edin',
      color: 'from-[#005A9E] to-[#003D6B]',
    },
    {
      icon: 'ri-bug-line',
      title: 'Zafiyet Yönetimi',
      description: 'Security Exposure Management ile saldırı yollarını görselleştirin ve zafiyetleri proaktif yönetin',
      color: 'from-[#003D6B] to-[#002050]',
    },
  ];

  const detectionMethods = [
    {
      name: 'Behavioral Analytics',
      description: 'Anormal davranış kalıplarını tespit eder',
      examples: ['Olağandışı süreç oluşturma', 'Beklenmeyen ağ bağlantıları', 'Kimlik bilgisi hırsızlığı girişimleri'],
      icon: 'ri-line-chart-line',
      color: 'bg-[#0078D4]/10 text-[#0078D4]',
    },
    {
      name: 'Machine Learning',
      description: 'Milyonlarca veri noktasından öğrenen AI modelleri',
      examples: ['Yeni malware varyantları', 'Zero-day saldırıları', 'Fileless malware'],
      icon: 'ri-brain-line',
      color: 'bg-[#00BCF2]/10 text-[#00BCF2]',
    },
    {
      name: 'Threat Intelligence',
      description: 'Microsoft Intelligent Security Graph entegrasyonu',
      examples: ['Bilinen IoC\'ler', 'APT grup taktikleri', 'Küresel tehdit trendleri'],
      icon: 'ri-global-line',
      color: 'bg-[#005A9E]/10 text-[#005A9E]',
    },
    {
      name: 'Signature-based',
      description: 'Geleneksel antivirüs imza taraması',
      examples: ['Bilinen malware aileleri', 'Yaygın exploit\'ler', 'Kötü amaçlı scriptler'],
      icon: 'ri-fingerprint-line',
      color: 'bg-[#003D6B]/10 text-[#003D6B]',
    },
  ];

  const airWorkflow = [
    {
      step: 'Tetikleme',
      description: 'Bir uyarı veya manuel başlatma ile soruşturma başlar',
      icon: 'ri-alarm-warning-line',
      color: 'bg-[#0078D4]',
    },
    {
      step: 'Veri Toplama',
      description: 'İlgili cihaz, kullanıcı ve ağ verileri toplanır',
      icon: 'ri-database-2-line',
      color: 'bg-[#00BCF2]',
    },
    {
      step: 'Analiz',
      description: 'AI algoritmaları tehdidi değerlendirir ve sınıflandırır',
      icon: 'ri-cpu-line',
      color: 'bg-[#005A9E]',
    },
    {
      step: 'Karar',
      description: 'Otomatik veya manuel onay ile yanıt eylemi belirlenir',
      icon: 'ri-checkbox-circle-line',
      color: 'bg-[#003D6B]',
    },
    {
      step: 'Yanıt',
      description: 'Dosya karantinası, cihaz izolasyonu gibi eylemler gerçekleştirilir',
      icon: 'ri-shield-flash-line',
      color: 'bg-[#002050]',
    },
  ];

  const tvmFeatures = [
    {
      title: 'Zafiyet Keşfi',
      description: 'Tüm endpoint\'lerde otomatik zafiyet taraması',
      icon: 'ri-search-line',
      metrics: ['Yazılım zafiyetleri', 'Yanlış yapılandırmalar', 'Eksik güncellemeler'],
    },
    {
      title: 'Risk Skorlama',
      description: 'CVSS ve Microsoft Threat Score ile önceliklendirme',
      icon: 'ri-bar-chart-box-line',
      metrics: ['Kritik: 9.0-10.0', 'Yüksek: 7.0-8.9', 'Orta: 4.0-6.9'],
    },
    {
      title: 'Düzeltme Önerileri',
      description: 'Otomatik düzeltme planları ve KB makaleleri',
      icon: 'ri-tools-line',
      metrics: ['Patch yönetimi', 'Yapılandırma değişiklikleri', 'Uygulama güncellemeleri'],
    },
    {
      title: 'Sürekli İzleme',
      description: 'Gerçek zamanlı zafiyet durumu takibi',
      icon: 'ri-eye-line',
      metrics: ['Exposure Score', 'Secure Score', 'Compliance durumu'],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#0078D4] to-[#005A9E] overflow-hidden">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 4px)',
            }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-8">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/edr"
              className="inline-flex items-center space-x-2 text-white/80 hover:text-white text-sm font-medium mb-8 cursor-pointer transition-colors"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              <span>EDR Sayfasına Dön</span>
            </Link>

            <div className="flex items-center space-x-3 mb-6">
              <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-md text-white text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                Endpoint Detection & Response
              </span>
              <span className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-md text-white text-xs font-semibold uppercase tracking-wider whitespace-nowrap">
                Microsoft Defender XDR
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-6">
              Gelişmiş Tehdit Koruması: Microsoft Defender for Endpoint Derinlemesine Rehberi
            </h1>

            <p className="text-lg text-white/85 leading-relaxed mb-8 max-w-3xl">
              EDR çözümü ile uç nokta tehditlerini tespit edin, araştırın ve otomatik yanıt mekanizmalarını yapılandırarak organizasyonunuzu gelişmiş siber tehditlere karşı koruyun.
            </p>

            <div className="flex items-center space-x-6 text-white/70 text-sm">
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-calendar-line text-base"></i>
                </div>
                <span>18 Ocak 2026</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-time-line text-base"></i>
                </div>
                <span>18 dk okuma</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-user-line text-base"></i>
                </div>
                <span>Özgür Çebi</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Makale İçeriği */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-8">
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <div className="text-gray-700 leading-relaxed space-y-6">

              {/* Giriş */}
              <p className="text-lg">
                Modern siber saldırılar giderek daha karmaşık hale geliyor. Geleneksel antivirüs çözümleri, imza tabanlı koruma ile bilinen tehditleri engelleyebilirken, zero-day saldırıları, fileless malware ve APT (Advanced Persistent Threat) gruplarının taktiklerine karşı yetersiz kalıyor. Microsoft Defender for Endpoint, <strong>Endpoint Detection and Response (EDR)</strong> yetenekleri ile bu gelişmiş tehditlere karşı kapsamlı koruma sağlar.
              </p>

              {/* MDE Nedir? */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Microsoft Defender for Endpoint Nedir?</h2>
                <p>
                  Microsoft Defender for Endpoint (MDE), bulut tabanlı bir endpoint güvenlik platformudur. Sadece antivirüs koruması değil, aynı zamanda tehdit tespiti, araştırma, otomatik yanıt ve zafiyet yönetimi gibi kapsamlı güvenlik yetenekleri sunar.
                </p>

                <div className="grid md:grid-cols-2 gap-6 my-8">
                  {capabilities.map((capability, index) => (
                    <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6">
                      <div className={`w-14 h-14 bg-gradient-to-br ${capability.color} rounded-xl flex items-center justify-center mb-4`}>
                        <i className={`${capability.icon} text-3xl text-white`}></i>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{capability.title}</h3>
                      <p className="text-sm text-gray-600">{capability.description}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* Microsoft Defender XDR Entegrasyonu */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Microsoft Defender XDR: Birleşik Güvenlik Platformu</h2>
                <p className="mb-6">
                  2026 itibarıyla, Microsoft Defender for Endpoint artık <strong>Microsoft Defender XDR</strong> (Extended Detection and Response) platformunun merkezi bir parçasıdır. Tüm güvenlik çözümleri tek bir portal altında birleşti: <a href="https://security.microsoft.com" target="_blank" rel="noopener noreferrer" className="text-[#0078D4] hover:underline">security.microsoft.com</a>
                </p>

                <div className="bg-gradient-to-br from-[#0078D4]/5 to-[#005A9E]/5 rounded-2xl p-8 mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Unified Agent Mimarisi</h3>
                  <p className="text-gray-700 mb-4">
                    Microsoft, tüm Defender ürünlerini tek bir "Unified Agent" (birleşik ajan) altında topladı. Bu, endpoint'lerde tek bir ajan ile şu yetenekleri sağlar:
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl p-4 border border-gray-200">
                      <div className="flex items-center mb-2">
                        <i className="ri-shield-check-line text-[#0078D4] text-xl mr-2"></i>
                        <h4 className="font-bold text-gray-900 text-sm">Endpoint Protection</h4>
                      </div>
                      <p className="text-xs text-gray-600">Next-gen AV, EDR, ASR kuralları</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-gray-200">
                      <div className="flex items-center mb-2">
                        <i className="ri-user-shield-line text-[#00BCF2] text-xl mr-2"></i>
                        <h4 className="font-bold text-gray-900 text-sm">Identity Protection</h4>
                      </div>
                      <p className="text-xs text-gray-600">Defender for Identity entegrasyonu</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-gray-200">
                      <div className="flex items-center mb-2">
                        <i className="ri-cloud-line text-[#005A9E] text-xl mr-2"></i>
                        <h4 className="font-bold text-gray-900 text-sm">Cloud Apps Security</h4>
                      </div>
                      <p className="text-xs text-gray-600">SaaS uygulama koruması</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border border-gray-200">
                      <div className="flex items-center mb-2">
                        <i className="ri-mail-line text-[#003D6B] text-xl mr-2"></i>
                        <h4 className="font-bold text-gray-900 text-sm">Office 365 Protection</h4>
                      </div>
                      <p className="text-xs text-gray-600">E-posta ve işbirliği araçları güvenliği</p>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-xl p-6">
                  <div className="flex items-start">
                    <i className="ri-information-line text-2xl text-blue-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">XDR'ın Gücü</h4>
                      <p className="text-gray-700 text-sm">
                        Unified Agent sayesinde, bir e-posta phishing saldırısından başlayan tehdit zincirini, endpoint'te yürütülen kötü amaçlı kod, kimlik hırsızlığı ve bulut uygulamalarına yetkisiz erişime kadar tek bir konsoldan takip edebilirsiniz. Bu, MTTD (Mean Time to Detect) ve MTTR (Mean Time to Respond) sürelerini dramatik şekilde azaltır.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Tehdit Tespit Yöntemleri */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Tehdit Tespit Yöntemleri</h2>
                <p className="mb-6">
                  MDE, çok katmanlı tespit mekanizmaları kullanarak bilinen ve bilinmeyen tehditleri tanımlar. Her yöntem, farklı saldırı vektörlerine karşı koruma sağlar.
                </p>

                <div className="space-y-4">
                  {detectionMethods.map((method, index) => (
                    <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#0078D4]/40 transition-colors">
                      <div className="flex items-start">
                        <div className={`w-12 h-12 ${method.color} rounded-xl flex items-center justify-center mr-4 flex-shrink-0`}>
                          <i className={`${method.icon} text-2xl`}></i>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-2">{method.name}</h3>
                          <p className="text-sm text-gray-600 mb-3">{method.description}</p>
                          <div className="bg-gray-50 rounded-lg p-4">
                            <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Tespit Edilen Tehditler:</h4>
                            <ul className="space-y-1">
                              {method.examples.map((example, idx) => (
                                <li key={idx} className="text-sm text-gray-600 flex items-start">
                                  <i className="ri-arrow-right-s-line text-[#0078D4] mr-1 mt-0.5 flex-shrink-0"></i>
                                  <span>{example}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-xl p-6 mt-8">
                  <div className="flex items-start">
                    <i className="ri-information-line text-2xl text-blue-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Çok Katmanlı Koruma</h4>
                      <p className="text-gray-700 text-sm">
                        MDE, bu dört tespit yöntemini aynı anda kullanarak "defense in depth" (derinlemesine savunma) stratejisi uygular. Bir yöntem atlatılsa bile, diğer katmanlar tehdidi yakalayabilir.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Deceptive Capabilities */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Deceptive Capabilities: Saldırganları Tuzağa Düşürün</h2>
                <p className="mb-6">
                  2026'da MDE'ye eklenen <strong>Deceptive Capabilities</strong> (Aldatıcı Yetenekler), saldırganları sahte varlıklarla tuzağa düşürerek erken tespit sağlar. Bu özellik, "Honeytoken" ve "Honeypot" konseptlerini endpoint seviyesine taşır.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-file-lock-line text-2xl text-amber-600"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Sahte Dosyalar</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Kritik görünen ancak içi boş dosyalar oluşturur (örn: "passwords.xlsx", "credentials.txt")
                    </p>
                    <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                      <strong>Senaryo:</strong> Saldırgan bu dosyayı açtığında veya kopyaladığında anında uyarı tetiklenir
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-user-forbid-line text-2xl text-purple-600"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Sahte Hesaplar</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Active Directory'de yüksek yetkili gibi görünen ancak hiçbir gerçek erişimi olmayan hesaplar
                    </p>
                    <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                      <strong>Senaryo:</strong> Lateral movement sırasında bu hesaba erişim denemesi yapıldığında alarm verilir
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-server-line text-2xl text-green-600"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Sahte Servisler</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Ağda kritik servisler gibi görünen ancak gerçekte izleme amaçlı çalışan honeypot'lar
                    </p>
                    <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                      <strong>Senaryo:</strong> Port taraması veya servis istismarı denemelerini gerçek zamanlı yakalar
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-key-line text-2xl text-red-600"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Sahte Kimlik Bilgileri</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Registry veya bellek içinde bırakılan sahte API anahtarları ve token'lar
                    </p>
                    <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                      <strong>Senaryo:</strong> Credential dumping araçları bu bilgileri topladığında tespit edilir
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-6">
                  <div className="flex items-start">
                    <i className="ri-lightbulb-line text-2xl text-amber-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Advanced Hunting ile Entegrasyon</h4>
                      <p className="text-gray-700 text-sm mb-3">
                        Deceptive Capabilities, Advanced Hunting ile mükemmel uyum sağlar. Sahte varlıklara yapılan erişim denemeleri, KQL sorguları ile analiz edilerek saldırganın taktiklerini (MITRE ATT&CK) haritalandırabilirsiniz.
                      </p>
                      <p className="text-gray-700 text-sm">
                        <strong>Örnek:</strong> Bir saldırgan sahte "admin_backup.txt" dosyasını açtığında, hangi süreçlerin bu dosyaya eriştiğini, hangi ağ bağlantılarının kurulduğunu ve hangi diğer dosyaların değiştirildiğini KQL ile sorgulayarak tam saldırı zincirini görebilirsiniz.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Advanced Hunting */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Advanced Hunting ile Tehdit Araştırma</h2>
                <p className="mb-6">
                  Advanced Hunting, 30 güne kadar olan endpoint verilerini KQL (Kusto Query Language) ile sorgulayarak proaktif tehdit avı yapmanızı sağlar. Güvenlik analistleri, özel sorgular yazarak gizli tehditleri ortaya çıkarabilir.
                </p>

                <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-6">
                  <div className="flex items-start">
                    <i className="ri-lightbulb-line text-2xl text-amber-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Proaktif Tehdit Avı İpuçları</h4>
                      <ul className="text-gray-700 text-sm space-y-1">
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>MITRE ATT&CK framework'ünü referans alarak sorgular oluşturun</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>Sık kullandığınız sorguları "Shared queries" olarak kaydedin</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>Tespit edilen anomalileri Custom Detection Rules'a dönüştürün</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>Haftalık düzenli tehdit avı oturumları planlayın</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Automated Investigation & Response */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Automated Investigation & Response (AIR)</h2>
                <p className="mb-6">
                  AIR, tehditlere otomatik olarak yanıt vererek güvenlik ekiplerinin iş yükünü azaltır. Bir uyarı tetiklendiğinde, AI destekli soruşturma süreci başlar ve gerekli düzeltme eylemlerini önerir veya otomatik olarak uygular.
                </p>

                <div className="bg-gradient-to-br from-[#0078D4]/5 to-[#005A9E]/5 rounded-2xl p-8 mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-6">AIR İş Akışı</h3>
                  <div className="space-y-6">
                    {airWorkflow.map((item, index) => (
                      <div key={index} className="flex items-start">
                        <div className="flex flex-col items-center mr-6">
                          <div className={`w-14 h-14 ${item.color} text-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}>
                            <i className={`${item.icon} text-2xl`}></i>
                          </div>
                          {index < airWorkflow.length - 1 && (
                            <div className="w-0.5 h-16 bg-gray-200 mt-2" />
                          )}
                        </div>
                        <div className="flex-1 pb-8">
                          <h4 className="text-lg font-bold text-gray-900 mb-2">{item.step}</h4>
                          <p className="text-sm text-gray-600">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-shield-flash-line text-[#0078D4] mr-2"></i>
                      Otomatik Yanıt Eylemleri
                    </h4>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-check-line text-[#0078D4] mr-2 mt-0.5"></i>
                        <span>Zararlı dosyaları karantinaya alma</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-[#0078D4] mr-2 mt-0.5"></i>
                        <span>Kötü amaçlı süreçleri sonlandırma</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-[#0078D4] mr-2 mt-0.5"></i>
                        <span>Cihazı ağdan izole etme</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-[#0078D4] mr-2 mt-0.5"></i>
                        <span>Kullanıcı hesabını devre dışı bırakma</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-[#0078D4] mr-2 mt-0.5"></i>
                        <span>Registry anahtarlarını düzeltme</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <h4 className="font-bold text-gray-900 mb-3 flex items-center">
                      <i className="ri-settings-3-line text-[#005A9E] mr-2"></i>
                      Otomasyon Seviyeleri
                    </h4>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-[#005A9E] mr-2 mt-0.5"></i>
                        <span><strong>Full (Level 5):</strong> Tüm eylemler otomatik uygulanır</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-[#005A9E] mr-2 mt-0.5"></i>
                        <span><strong>Semi (Level 3):</strong> Kritik eylemler onay gerektirir</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-[#005A9E] mr-2 mt-0.5"></i>
                        <span><strong>No automation (Level 1):</strong> Sadece öneriler sunulur</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-xl p-6">
                  <div className="flex items-start">
                    <i className="ri-information-line text-2xl text-blue-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Level 5 - Full Automation Yapılandırması</h4>
                      <p className="text-gray-700 text-sm mb-3">
                        2026'da önerilen en iyi uygulama, olgun güvenlik ekipleri için <strong>Level 5 (Full Automation)</strong> modunu kullanmaktır. Bu mod, tehditlere milisaniyeler içinde yanıt vererek saldırı zincirini kırar.
                      </p>
                      <p className="text-gray-700 text-sm">
                        <strong>Yapılandırma:</strong> Settings &gt; Endpoints &gt; Advanced features &gt; Automated investigation &gt; "Full - remediate threats automatically" seçeneğini etkinleştirin. Kritik sunucular için özel cihaz grupları oluşturarak bu gruplarda Semi-automated mod kullanabilirsiniz.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Security Exposure Management */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Security Exposure Management: Yeni Nesil Zafiyet Yönetimi</h2>
                <p className="mb-6">
                  2025 sonu itibarıyla, Microsoft Defender for Endpoint'in zafiyet yönetimi modülü <strong>Security Exposure Management</strong> adı altında yeniden yapılandırıldı. Artık sadece CVE'leri listelemekle kalmıyor, aynı zamanda <strong>saldırı yollarını (attack paths)</strong> görselleştirerek hangi zafiyetlerin gerçek risk oluşturduğunu gösteriyor.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  {tvmFeatures.map((feature, index) => (
                    <div key={index} className="bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#0078D4]/40 transition-colors">
                      <div className="w-12 h-12 bg-[#0078D4]/10 rounded-xl flex items-center justify-center mb-4">
                        <i className={`${feature.icon} text-2xl text-[#0078D4]`}></i>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                      <p className="text-sm text-gray-600 mb-4">{feature.description}</p>
                      <div className="bg-gray-50 rounded-lg p-3">
                        <ul className="space-y-1">
                          {feature.metrics.map((metric, idx) => (
                            <li key={idx} className="text-xs text-gray-600 flex items-start">
                              <i className="ri-checkbox-circle-line text-[#0078D4] mr-1.5 mt-0.5 flex-shrink-0"></i>
                              <span>{metric}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="bg-gradient-to-br from-[#0078D4]/5 to-[#005A9E]/5 rounded-2xl p-8 mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Attack Path Visualization (Saldırı Yolu Görselleştirme)</h3>
                  <p className="text-gray-700 mb-4">
                    Security Exposure Management'ın en güçlü özelliği, bir saldırganın ağınızda nasıl ilerleyebileceğini görsel olarak göstermesidir. Örneğin:
                  </p>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-start space-x-4">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 mb-3">Örnek Saldırı Yolu:</h4>
                        <div className="space-y-3">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">1</div>
                            <div className="flex-1">
                              <p className="text-sm text-gray-700"><strong>Başlangıç:</strong> Kullanıcı workstation'ında CVE-2024-XXXX zafiyeti (CVSS 7.8)</p>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">2</div>
                            <div className="flex-1">
                              <p className="text-sm text-gray-700"><strong>Lateral Movement:</strong> SMB üzerinden file server'a erişim (yanlış yapılandırılmış izinler)</p>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-yellow-500 text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">3</div>
                            <div className="flex-1">
                              <p className="text-sm text-gray-700"><strong>Privilege Escalation:</strong> Domain Controller'da eski Windows Server 2016 (patch eksikliği)</p>
                            </div>
                          </div>
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">4</div>
                            <div className="flex-1">
                              <p className="text-sm text-gray-700"><strong>Hedef:</strong> Domain Admin yetkisi ele geçirme</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm mt-4">
                    Bu görselleştirme sayesinde, sadece CVSS skoruna bakmak yerine, <strong>gerçek iş etkisi</strong> olan zafiyetleri önceliklendirirsiniz. Yukarıdaki örnekte, workstation'daki 7.8 CVSS skorlu zafiyet, Domain Controller'a giden bir saldırı yolunun parçası olduğu için kritik öncelik kazanır.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                        <i className="ri-alert-line text-2xl text-red-600"></i>
                      </div>
                      <h4 className="font-bold text-gray-900">Exposure Score</h4>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">
                      Organizasyonunuzun mevcut zafiyet durumunu 0-1000 arasında skorlar. Yüksek skor, daha fazla risk anlamına gelir.
                    </p>
                    <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                      <strong>Hedef:</strong> Exposure Score'u düşürmek için kritik saldırı yollarını kırın
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                        <i className="ri-shield-check-line text-2xl text-green-600"></i>
                      </div>
                      <h4 className="font-bold text-gray-900">Secure Score</h4>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">
                      Güvenlik yapılandırmalarınızın ne kadar iyi olduğunu ölçer. Yüksek skor, daha iyi güvenlik duruşu anlamına gelir.
                    </p>
                    <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                      <strong>Hedef:</strong> Önerilen güvenlik yapılandırmalarını uygulayarak skoru artırın
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-6">
                  <div className="flex items-start">
                    <i className="ri-lightbulb-line text-2xl text-amber-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Exposure Management En İyi Uygulamaları</h4>
                      <ul className="text-gray-700 text-sm space-y-1">
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>Haftalık "Attack Path Review" toplantıları düzenleyin</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>Kritik varlıklara (DC, DB sunucuları) giden saldırı yollarını 48 saat içinde kapatın</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>Düzeltme görevlerini Intune veya SCCM ile otomatikleştirin</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>Exposure Score trendlerini aylık olarak yönetime raporlayın</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Çoklu Platform Desteği */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Çoklu Platform Desteği</h2>
                <p className="mb-6">
                  Microsoft Defender for Endpoint, sadece Windows değil, Linux, macOS, iOS ve Android platformlarında da kapsamlı koruma sağlar. 2026 itibarıyla tüm platformlar için Unified Agent kullanılmaktadır.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                        <i className="ri-windows-line text-2xl text-blue-600"></i>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Windows</h3>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-check-line text-blue-600 mr-2 mt-0.5"></i>
                        <span><strong>Desteklenen:</strong> Windows 10/11, Server 2016/2019/2022/2025</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-blue-600 mr-2 mt-0.5"></i>
                        <span><strong>Özellikler:</strong> Full EDR, ASR kuralları, TVM, Deceptive Capabilities</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-blue-600 mr-2 mt-0.5"></i>
                        <span><strong>Yeni:</strong> Windows Server 2025 için gelişmiş container güvenliği</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                        <i className="ri-ubuntu-line text-2xl text-orange-600"></i>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Linux</h3>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-check-line text-orange-600 mr-2 mt-0.5"></i>
                        <span><strong>Desteklenen:</strong> RHEL, Ubuntu, SLES, Debian, CentOS</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-orange-600 mr-2 mt-0.5"></i>
                        <span><strong>Özellikler:</strong> EDR, Real-time protection, Custom indicators</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-orange-600 mr-2 mt-0.5"></i>
                        <span><strong>Yeni:</strong> eBPF desteği ile kernel-level tehdit tespiti</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                        <i className="ri-apple-line text-2xl text-gray-700"></i>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">macOS</h3>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-check-line text-gray-700 mr-2 mt-0.5"></i>
                        <span><strong>Desteklenen:</strong> macOS 11 (Big Sur) ve üzeri</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-gray-700 mr-2 mt-0.5"></i>
                        <span><strong>Özellikler:</strong> EDR, Real-time protection, Network protection</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-alert-line text-amber-600 mr-2 mt-0.5"></i>
                        <span><strong>Kısıt:</strong> ASR kuralları ve bazı AIR özellikleri sınırlı</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                        <i className="ri-smartphone-line text-2xl text-green-600"></i>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">iOS & Android</h3>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex items-start">
                        <i className="ri-check-line text-green-600 mr-2 mt-0.5"></i>
                        <span><strong>Desteklenen:</strong> iOS 15+, Android 8.0+</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-green-600 mr-2 mt-0.5"></i>
                        <span><strong>Özellikler:</strong> Phishing protection, Jailbreak/root detection</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-check-line text-green-600 mr-2 mt-0.5"></i>
                        <span><strong>Yeni:</strong> Network Protection - Kötü amaçlı Wi-Fi ve VPN tespiti</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-xl p-6">
                  <div className="flex items-start">
                    <i className="ri-information-line text-2xl text-blue-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Linux eBPF Desteği</h4>
                      <p className="text-gray-700 text-sm">
                        2025 sonunda eklenen eBPF (extended Berkeley Packet Filter) desteği, Linux sistemlerde kernel seviyesinde tehdit tespiti sağlar. Bu, rootkit'ler ve kernel-mode malware'lere karşı koruma için kritik öneme sahiptir. eBPF, sistem performansını etkilemeden gerçek zamanlı izleme yapar ve container ortamlarında (Docker, Kubernetes) mükemmel çalışır.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Offline ve Hava Boşluklu Ortamlar */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Offline Ortamlar</h2>
                <p className="mb-6">
                  Kurumsal ortamlarda, özellikle askeri, finans ve kritik altyapı sektörlerinde, bazı cihazlar güvenlik nedeniyle internete doğrudan erişemez. MDE, bu tür ortamlar için özel yapılandırma seçenekleri sunar.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-router-line text-2xl text-purple-600"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Proxy/Gateway Desteği</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      İnternete doğrudan erişimi olmayan cihazlar, kurumsal proxy veya gateway üzerinden MDE bulut hizmetlerine bağlanabilir.
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Yapılandırma:</h4>
                      <ul className="space-y-1 text-xs text-gray-600">
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-purple-600 mr-1 mt-0.5"></i>
                          <span>Static proxy ayarları (HTTP/HTTPS)</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-purple-600 mr-1 mt-0.5"></i>
                          <span>PAC (Proxy Auto-Configuration) dosyası desteği</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-purple-600 mr-1 mt-0.5"></i>
                          <span>Authenticated proxy (NTLM/Kerberos)</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-purple-600 mr-1 mt-0.5"></i>
                          <span>TLS inspection bypass (SSL pinning)</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-shield-cross-line text-2xl text-red-600"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Offline Definition Updates</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Tamamen izole ortamlar için antivirüs imza güncellemeleri manuel olarak dağıtılabilir.
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Yöntemler:</h4>
                      <ul className="space-y-1 text-xs text-gray-600">
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-red-600 mr-1 mt-0.5"></i>
                          <span>WSUS (Windows Server Update Services)</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-red-600 mr-1 mt-0.5"></i>
                          <span>SCCM/Intune ile paket dağıtımı</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-red-600 mr-1 mt-0.5"></i>
                          <span>Manuel VDM (Virus Definition Manager) indirme</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-red-600 mr-1 mt-0.5"></i>
                          <span>USB/Removable media ile güncelleme</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-lock-line text-2xl text-amber-600"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Disconnected Mode (Bağlantısız Mod)</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Tamamen hava boşluklu (air-gapped) ortamlar için sınırlı koruma modu.
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Özellikler:</h4>
                      <ul className="space-y-1 text-xs text-gray-600">
                        <li className="flex items-start">
                          <i className="ri-check-line text-green-600 mr-1 mt-0.5"></i>
                          <span>Local signature-based scanning</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-check-line text-green-600 mr-1 mt-0.5"></i>
                          <span>Behavioral monitoring (sınırlı)</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-close-line text-red-600 mr-1 mt-0.5"></i>
                          <span>Cloud-based AI/ML koruması yok</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-close-line text-red-600 mr-1 mt-0.5"></i>
                          <span>Advanced Hunting ve AIR kullanılamaz</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-upload-cloud-line text-2xl text-blue-600"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Batch Telemetry Upload</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Periyodik bağlantı kurabilen cihazlar için toplu veri gönderimi.
                    </p>
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Kullanım Senaryoları:</h4>
                      <ul className="space-y-1 text-xs text-gray-600">
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-blue-600 mr-1 mt-0.5"></i>
                          <span>Günde 1-2 kez VPN bağlantısı kuran cihazlar</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-blue-600 mr-1 mt-0.5"></i>
                          <span>Saha çalışanlarının laptop'ları</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-blue-600 mr-1 mt-0.5"></i>
                          <span>Uydu bağlantılı uzak lokasyonlar</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-blue-600 mr-1 mt-0.5"></i>
                          <span>Bant genişliği kısıtlı ortamlar</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl p-8 mb-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Kurumsal Proxy Yapılandırma Örneği</h3>
                  <p className="text-gray-700 text-sm mb-4">
                    Büyük ölçekli kurumsal ortamlarda, tüm endpoint trafiği merkezi bir proxy üzerinden geçer. MDE için gerekli URL'lerin beyaz listeye eklenmesi kritik öneme sahiptir.
                  </p>
                  <CodeBlock
                    title="PowerShell - Proxy Yapılandırması"
                    code={`# MDE için proxy ayarları
Set-MpPreference -ProxyServer "http://proxy.contoso.com:8080"
Set-MpPreference -ProxyBypass "localhost;127.0.0.1;*.contoso.com"

# Authenticated proxy için
Set-MpPreference -ProxyPacUrl "http://proxy.contoso.com/proxy.pac"

# Proxy ayarlarını doğrulama
Get-MpPreference | Select-Object ProxyServer, ProxyBypass, ProxyPacUrl

# MDE bağlantı testi
Test-NetConnection -ComputerName "winatp-gw-cus.microsoft.com" -Port 443`}
                  />
                  <div className="bg-white/70 rounded-lg p-4 mt-4">
                    <h4 className="text-sm font-bold text-gray-900 mb-2">Beyaz Listeye Eklenecek URL'ler:</h4>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-purple-600 mr-1 mt-0.5"></i>
                        <span>*.blob.core.windows.net (Telemetri ve güncellemeler)</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-purple-600 mr-1 mt-0.5"></i>
                        <span>*.microsoft.com (Bulut hizmetleri)</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-purple-600 mr-1 mt-0.5"></i>
                        <span>winatp-gw-*.microsoft.com (EDR gateway)</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-purple-600 mr-1 mt-0.5"></i>
                        <span>*.security.microsoft.com (Portal erişimi)</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-6">
                  <div className="flex items-start">
                    <i className="ri-alert-line text-2xl text-amber-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Kritik Uyarı: Air-Gapped Ortamlarda Sınırlamalar</h4>
                      <p className="text-gray-700 text-sm mb-3">
                        Tamamen izole ortamlarda MDE'nin en güçlü özellikleri (bulut tabanlı AI/ML, Advanced Hunting, AIR, Deceptive Capabilities) kullanılamaz. Bu ortamlar için:
                      </p>
                      <ul className="text-gray-700 text-sm space-y-1">
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>Günlük imza güncellemelerini manuel olarak dağıtın</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>Local event log'ları düzenli olarak SIEM'e aktarın</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>Ek güvenlik katmanları (IDS/IPS, Network Segmentation) ekleyin</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>Periyodik güvenlik denetimleri ve penetrasyon testleri yapın</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Entegrasyon ve Ekosistem */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Microsoft Defender XDR Ekosistemi</h2>
                <p className="mb-6">
                  MDE, Microsoft Defender XDR ekosisteminin merkezi bir parçası olarak diğer güvenlik çözümleri ile entegre çalışır. Unified Agent sayesinde, tüm bu entegrasyonlar tek bir ajan üzerinden gerçekleşir.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-[#0078D4]/10 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-mail-line text-2xl text-[#0078D4]"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Defender for Office 365</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      E-posta tehditleri ile endpoint saldırıları arasındaki bağlantıyı görün
                    </p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-[#0078D4] mr-1 mt-0.5"></i>
                        <span>Phishing e-postalarından başlayan saldırı zincirlerini takip edin</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-[#0078D4] mr-1 mt-0.5"></i>
                        <span>Kötü amaçlı eklerin endpoint'te yürütülmesini engelleyin</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-[#00BCF2]/10 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-user-shield-line text-2xl text-[#00BCF2]"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Defender for Identity</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      Kimlik tabanlı saldırıları endpoint aktiviteleri ile ilişkilendirin
                    </p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-[#00BCF2] mr-1 mt-0.5"></i>
                        <span>Pass-the-hash ve pass-the-ticket saldırılarını tespit edin</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-[#00BCF2] mr-1 mt-0.5"></i>
                        <span>Lateral movement girişimlerini gerçek zamanlı engelleyin</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-[#005A9E]/10 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-cloud-line text-2xl text-[#005A9E]"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Defender for Cloud Apps</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      SaaS uygulamalarındaki tehditleri endpoint güvenliği ile birleştirin
                    </p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-[#005A9E] mr-1 mt-0.5"></i>
                        <span>Shadow IT kullanımını tespit edin ve kontrol edin</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-[#005A9E] mr-1 mt-0.5"></i>
                        <span>Bulut uygulamalarına anormal erişimleri engelleyin</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white border-2 border-gray-200 rounded-xl p-6">
                    <div className="w-12 h-12 bg-[#003D6B]/10 rounded-xl flex items-center justify-center mb-4">
                      <i className="ri-radar-line text-2xl text-[#003D6B]"></i>
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">Microsoft Sentinel</h3>
                    <p className="text-sm text-gray-600 mb-3">
                      SIEM entegrasyonu ile tüm güvenlik verilerini merkezi analiz edin
                    </p>
                    <ul className="text-xs text-gray-600 space-y-1">
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-[#003D6B] mr-1 mt-0.5"></i>
                        <span>MDE uyarılarını diğer log kaynaklarıyla ilişkilendirin</span>
                      </li>
                      <li className="flex items-start">
                        <i className="ri-arrow-right-s-line text-[#003D6B] mr-1 mt-0.5"></i>
                        <span>Gelişmiş analitik ve makine öğrenimi uygulayın</span>
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-[#0078D4]/5 to-[#005A9E]/5 rounded-2xl p-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">XDR Incident Response Örneği</h3>
                  <p className="text-gray-700 mb-4">
                    Unified Agent ve XDR entegrasyonu sayesinde, bir güvenlik olayı tüm katmanlarda otomatik olarak ilişkilendirilir:
                  </p>
                  <div className="bg-white rounded-xl p-6 border border-gray-200">
                    <div className="space-y-4">
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-[#0078D4] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">1</div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">Defender for Office 365</p>
                          <p className="text-xs text-gray-600">Kullanıcı bir phishing e-postasındaki kötü amaçlı linke tıklar</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-[#00BCF2] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">2</div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">Defender for Endpoint</p>
                          <p className="text-xs text-gray-600">Endpoint'te kötü amaçlı PowerShell scripti çalıştırılır ve tespit edilir</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-[#005A9E] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">3</div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">Defender for Identity</p>
                          <p className="text-xs text-gray-600">Kullanıcı hesabından anormal kimlik doğrulama denemeleri tespit edilir</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-[#003D6B] text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">4</div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">Defender for Cloud Apps</p>
                          <p className="text-xs text-gray-600">Ele geçirilen hesaptan OneDrive'a anormal veri indirme aktivitesi görülür</p>
                        </div>
                      </div>
                      <div className="flex items-start">
                        <div className="w-8 h-8 bg-green-600 text-white rounded-full flex items-center justify-center text-xs font-bold mr-3 flex-shrink-0">✓</div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">Otomatik Yanıt (AIR)</p>
                          <p className="text-xs text-gray-600">Tüm katmanlarda koordineli yanıt: Endpoint izolasyonu, hesap devre dışı, e-posta karantina, bulut erişimi engelleme</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-700 text-sm mt-4">
                    Bu tüm süreç, <strong>security.microsoft.com</strong> portalında tek bir "Incident" olarak görüntülenir ve tüm adımlar otomatik olarak ilişkilendirilir. Güvenlik analisti, farklı konsollara geçmeden tüm saldırı zincirini görebilir.
                  </p>
                </div>
              </section>

              {/* Lisans ve Gereksinimler */}
              <section className="mb-12">
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Lisans Gereksinimleri (Plan 1 vs Plan 2)</h2>
                <p className="mb-6">
                  MDE, farklı lisans seviyeleri ile sunulur. İhtiyaçlarınıza göre uygun planı seçebilirsiniz. Aşağıdaki tablo, Plan 1 (E3) ve Plan 2 (E5) arasındaki farkları detaylı göstermektedir.
                </p>

                <div className="overflow-x-auto mb-8">
                  <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-md">
                    <thead>
                      <tr className="bg-gradient-to-r from-[#0078D4] to-[#005A9E] text-white">
                        <th className="px-6 py-4 text-left text-sm font-bold">Özellik</th>
                        <th className="px-6 py-4 text-center text-sm font-bold">Plan 1 (E3)</th>
                        <th className="px-6 py-4 text-center text-sm font-bold">Plan 2 (E5)</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm">
                      <tr className="border-b border-gray-200">
                        <td className="px-6 py-4 font-medium text-gray-900">Next-generation Protection (Antivirüs)</td>
                        <td className="px-6 py-4 text-center"><i className="ri-check-line text-green-600 text-xl"></i></td>
                        <td className="px-6 py-4 text-center"><i className="ri-check-line text-green-600 text-xl"></i></td>
                      </tr>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <td className="px-6 py-4 font-medium text-gray-900">Attack Surface Reduction (ASR) Kuralları</td>
                        <td className="px-6 py-4 text-center"><i className="ri-check-line text-green-600 text-xl"></i></td>
                        <td className="px-6 py-4 text-center"><i className="ri-check-line text-green-600 text-xl"></i></td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-6 py-4 font-medium text-gray-900">Cihaz Kontrolü ve Web Koruması</td>
                        <td className="px-6 py-4 text-center"><i className="ri-check-line text-green-600 text-xl"></i></td>
                        <td className="px-6 py-4 text-center"><i className="ri-check-line text-green-600 text-xl"></i></td>
                      </tr>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <td className="px-6 py-4 font-medium text-gray-900">Temel Raporlama</td>
                        <td className="px-6 py-4 text-center"><i className="ri-check-line text-green-600 text-xl"></i></td>
                        <td className="px-6 py-4 text-center"><i className="ri-check-line text-green-600 text-xl"></i></td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-6 py-4 font-medium text-gray-900"><strong>EDR Yetenekleri</strong> (Tehdit Tespiti ve Yanıt)</td>
                        <td className="px-6 py-4 text-center"><i className="ri-close-line text-red-600 text-xl"></i></td>
                        <td className="px-6 py-4 text-center"><i className="ri-check-line text-green-600 text-xl"></i></td>
                      </tr>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <td className="px-6 py-4 font-medium text-gray-900"><strong>Advanced Hunting</strong> (KQL Sorguları)</td>
                        <td className="px-6 py-4 text-center"><i className="ri-close-line text-red-600 text-xl"></i></td>
                        <td className="px-6 py-4 text-center"><i className="ri-check-line text-green-600 text-xl"></i></td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-6 py-4 font-medium text-gray-900"><strong>Automated Investigation & Response (AIR)</strong></td>
                        <td className="px-6 py-4 text-center"><i className="ri-close-line text-red-600 text-xl"></i></td>
                        <td className="px-6 py-4 text-center"><i className="ri-check-line text-green-600 text-xl"></i></td>
                      </tr>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <td className="px-6 py-4 font-medium text-gray-900"><strong>Security Exposure Management</strong> (TVM + Attack Paths)</td>
                        <td className="px-6 py-4 text-center"><i className="ri-subtract-line text-amber-600 text-xl"></i><br/><span className="text-xs text-gray-600">Sınırlı</span></td>
                        <td className="px-6 py-4 text-center"><i className="ri-check-line text-green-600 text-xl"></i><br/><span className="text-xs text-gray-600">Tam</span></td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="px-6 py-4 font-medium text-gray-900">Çoklu Platform Desteği (Linux, macOS, Mobile)</td>
                        <td className="px-6 py-4 text-center"><i className="ri-check-line text-green-600 text-xl"></i></td>
                        <td className="px-6 py-4 text-center"><i className="ri-check-line text-green-600 text-xl"></i></td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white border-2 border-[#0078D4] rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-[#0078D4] rounded-lg flex items-center justify-center mr-3">
                        <i className="ri-shield-check-line text-2xl text-white"></i>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Plan 1 (E3)</h3>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">
                      <strong>Kimler için uygun:</strong> Temel endpoint koruması arayan, bütçe kısıtı olan veya küçük-orta ölçekli organizasyonlar
                    </p>
                    <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                      <strong>Önemli Not:</strong> Plan 1 ile EDR, Advanced Hunting, AIR ve TVM özellikleri kullanılamaz. Sadece antivirüs ve ASR kuralları mevcuttur.
                    </div>
                  </div>

                  <div className="bg-white border-2 border-[#005A9E] rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <div className="w-12 h-12 bg-[#005A9E] rounded-lg flex items-center justify-center mr-3">
                        <i className="ri-shield-star-line text-2xl text-white"></i>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900">Plan 2 (E5)</h3>
                    </div>
                    <p className="text-sm text-gray-700 mb-3">
                      <strong>Kimler için uygun:</strong> Gelişmiş tehdit tespiti, proaktif tehdit avı ve otomatik yanıt gerektiren kurumsal organizasyonlar
                    </p>
                    <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-600">
                      <strong>Önerilen:</strong> Bu makalede bahsedilen tüm EDR yetenekleri için Plan 2 gereklidir. Ciddi güvenlik ihtiyacı olan organizasyonlar için vazgeçilmezdir.
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-xl p-6 mt-6">
                  <div className="flex items-start">
                    <i className="ri-information-line text-2xl text-amber-600 mr-4 mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-2">Lisans Seçim Rehberi</h4>
                      <p className="text-gray-700 text-sm mb-3">
                        Eğer organizasyonunuz aşağıdaki sorulardan herhangi birine "evet" cevabı veriyorsa, <strong>Plan 2 (E5)</strong> lisansına ihtiyacınız var:
                      </p>
                      <ul className="text-gray-700 text-sm space-y-1">
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>Gelişmiş APT saldırılarına karşı korunmak istiyor musunuz?</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>Proaktif tehdit avı (threat hunting) yapacak bir SOC ekibiniz var mı?</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>Otomatik yanıt vermek istiyor musunuz?</span>
                        </li>
                        <li className="flex items-start">
                          <i className="ri-arrow-right-s-line text-amber-600 mr-1 mt-0.5"></i>
                          <span>Zafiyet yönetimini saldırı yolları ile önceliklendirmek istiyor musunuz?</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </section>

              {/* Sonuç */}
              <section className="mb-12">
                <div className="bg-gradient-to-br from-[#0078D4] to-[#005A9E] text-white rounded-2xl p-8">
                  <h2 className="text-2xl font-bold mb-4">Özet ve Sonraki Adımlar</h2>
                  <p className="text-white/90 mb-6">
                    Microsoft Defender for Endpoint, 2026 itibarıyla modern endpoint güvenliğinin en kapsamlı çözümüdür. Unified Agent mimarisi, XDR entegrasyonu, Security Exposure Management, Deceptive Capabilities ve çoklu platform desteği ile organizasyonunuzu gelişmiş tehditlere karşı korur. Başarılı bir MDE dağıtımı için önce Plan 2 lisansını edinin, ardından Advanced Hunting ile tehdit avı yapmaya başlayın, AIR'ı Level 5 (Full Automation) olarak yapılandırın ve Security Exposure Management ile saldırı yollarını sürekli izleyin.
                  </p>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4">
                      <div className="text-2xl font-bold mb-1">30 Gün</div>
                      <div className="text-white/80 text-sm">Advanced Hunting Veri Saklama</div>
                    </div>
                    <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4">
                      <div className="text-2xl font-bold mb-1">%95</div>
                      <div className="text-white/80 text-sm">Otomatik Tehdit Düzeltme Oranı (AIR Level 5)</div>
                    </div>
                    <div className="bg-white/15 backdrop-blur-sm rounded-xl p-4">
                      <div className="text-2xl font-bold mb-1">24/7</div>
                      <div className="text-white/80 text-sm">Sürekli Tehdit İzleme (Tüm Platformlar)</div>
                    </div>
                  </div>
                </div>
              </section>

              {/* İlgili Makaleler */}
              <section className="mt-16 pt-12 border-t-2 border-gray-200">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">İlgili Makaleler</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  <Link
                    to="/edr/asr-kurallari"
                    className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#0078D4] transition-all"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0078D4] transition-colors">
                      ASR Kuralları ile Saldırı Yüzeyi Azaltma
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      Attack Surface Reduction kurallarını yapılandırarak bilinen saldırı vektörlerine karşı proaktif koruma sağlayın
                    </p>
                    <span className="text-[#0078D4] text-sm font-medium">
                      Devamını Oku <i className="ri-arrow-right-line ml-1"></i>
                    </span>
                  </Link>

                  <Link
                    to="/sentinel/sentinel-kurulum-rehberi"
                    className="group bg-white border-2 border-gray-200 rounded-xl p-6 hover:border-[#0078D4] transition-all"
                  >
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-[#0078D4] transition-colors">
                      Sentinel Kurulum Rehberi
                    </h3>
                    <p className="text-gray-600 text-sm mb-4">
                      MDE verilerini Sentinel ile entegre ederek gelişmiş SIEM analizi yapın
                    </p>
                    <span className="text-[#0078D4] text-sm font-medium">
                      Devamını Oku <i className="ri-arrow-right-line ml-1"></i>
                    </span>
                  </Link>
                </div>
              </section>

            </div>
          </motion.article>
        </div>
      </section>

      {/* Alt Navigasyon */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-4xl mx-auto px-8">
          <div className="flex items-center justify-between">
            <Link
              to="/edr"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all cursor-pointer text-gray-700 hover:text-[#0078D4]"
            >
              <i className="ri-arrow-left-line text-lg"></i>
              <span className="text-sm font-medium whitespace-nowrap">EDR Sayfası</span>
            </Link>
            <Link
              to="/"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all cursor-pointer text-gray-700 hover:text-[#0078D4]"
            >
              <span className="text-sm font-medium whitespace-nowrap">Ana Sayfa</span>
              <i className="ri-arrow-right-line text-lg"></i>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default DefenderForEndpointPage;
